import type { CalendarDayShift } from "@/shared/repositories/WorkOrderRepository";
import { getMomentTz, TimeService } from "@dexteel/mesf-core";
import "moment-duration-format";

const moment = getMomentTz();
const serverTz = () => TimeService.getInstance().getServerTimeZone();

export const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** plain left only; ctrl+click = Mac right-click */
export const isPlainLeft = (e: { button: number; ctrlKey: boolean }) =>
	e.button === 0 && !e.ctrlKey;

export const startOfMonth = (date: Date) =>
	new Date(date.getFullYear(), date.getMonth(), 1);

export const endOfMonth = (date: Date) =>
	new Date(date.getFullYear(), date.getMonth() + 1, 0);

export const startOfWeek = (date: Date, startsOn: number = 1) => {
	const day = date.getDay();
	const diff = (day < startsOn ? 7 : 0) + day - startsOn;
	const result = new Date(date);
	result.setDate(date.getDate() - diff);
	return result;
};

export const addDays = (date: Date, days: number) => {
	const result = new Date(date);
	result.setDate(date.getDate() + days);
	return result;
};

export const isSameDay = (date1: Date, date2: Date) =>
	date1.getFullYear() === date2.getFullYear() &&
	date1.getMonth() === date2.getMonth() &&
	date1.getDate() === date2.getDate();

export const isToday = (date: Date) => isSameDay(date, new Date());

export const isDayInCurrentMonth = (date: Date, currentDate: Date): boolean =>
	date.getMonth() === currentDate.getMonth() &&
	date.getFullYear() === currentDate.getFullYear();

export const toDateKey = (date: Date) => {
	const m = `${date.getMonth() + 1}`.padStart(2, "0");
	const d = `${date.getDate()}`.padStart(2, "0");
	return `${date.getFullYear()}-${m}-${d}`;
};

// Weeks (Mon-Sun) covering the whole month of `currentDate`
export const getMonthWeeks = (currentDate: Date): Date[][] => {
	const weeks: Date[][] = [];
	let cursor = startOfWeek(startOfMonth(currentDate), 1);
	const monthEnd = endOfMonth(currentDate);

	while (cursor <= monthEnd) {
		const week: Date[] = [];
		for (let i = 0; i < 7; i++) {
			week.push(addDays(cursor, i));
		}
		weeks.push(week);
		cursor = addDays(cursor, 7);
	}

	return weeks;
};

// ---- Week segments (mill EnhancedMonthCalendar port) ----
// A multi-day event renders as ONE continuous bar per week row, positioned
// and sized proportionally to its hours in the server timezone.

export interface SegmentItem {
	key: string;
	start: Date;
	end: Date;
	/** row-stacking tiebreaker for items with identical windows (WO Sequence) */
	sortHint: number;
	/** rendered as a small chip and stacked below every regular bar (breaks) */
	compact?: boolean;
}

export interface WeekSegment<T extends SegmentItem> {
	item: T;
	weekIndex: number;
	firstDayInWeek: number;
	lastDayInWeek: number;
	spanDays: number;
	isStart: boolean;
	isEnd: boolean;
}

export const buildWeekSegments = <T extends SegmentItem>(
	items: T[],
	weeks: Date[][],
): WeekSegment<T>[] => {
	const tz = serverTz();
	const segments: WeekSegment<T>[] = [];

	items.forEach((item) => {
		const itemStart = moment.tz(item.start, tz);
		const itemEnd = moment.tz(item.end, tz);

		weeks.forEach((week, weekIndex) => {
			let first = -1;
			let last = -1;

			week.forEach((day, dayIndex) => {
				const dayStart = moment.tz(toDateKey(day), "YYYY-MM-DD", tz);
				const dayEnd = dayStart.clone().endOf("day");
				if (itemStart.isBefore(dayEnd) && itemEnd.isAfter(dayStart)) {
					if (first === -1) first = dayIndex;
					last = dayIndex;
				}
			});

			if (first === -1) return;
			segments.push({
				item,
				weekIndex,
				firstDayInWeek: first,
				lastDayInWeek: last,
				spanDays: last - first + 1,
				isStart: itemStart.format("YYYY-MM-DD") === toDateKey(week[first]),
				isEnd: itemEnd.format("YYYY-MM-DD") === toDateKey(week[last]),
			});
		});
	});

	return segments;
};

// Bars shorter than this claim the width anyway: their label overflows to the
// right, so a close neighbor drops to the next row instead of covering it
const MIN_VISUAL_HOURS = 7;
const MIN_VISUAL_HOURS_COMPACT = 2;

const packRows = <T extends SegmentItem>(
	segments: WeekSegment<T>[],
	minVisualHours: number,
): WeekSegment<T>[][] => {
	const visualEnd = (item: SegmentItem) =>
		Math.max(
			item.end.getTime(),
			item.start.getTime() + minVisualHours * 3_600_000,
		);

	const sorted = [...segments].sort(
		(a, b) =>
			a.item.start.getTime() - b.item.start.getTime() ||
			a.item.sortHint - b.item.sortHint,
	);

	const rows: WeekSegment<T>[][] = [];
	sorted.forEach((seg) => {
		const row = rows.find((r) =>
			r.every(
				(other) =>
					seg.item.start.getTime() >= visualEnd(other.item) ||
					visualEnd(seg.item) <= other.item.start.getTime(),
			),
		);
		if (row) row.push(seg);
		else rows.push([seg]);
	});

	return rows;
};

// Greedy row stacking: segments of one week share a row only if their visual
// spans (time window widened to the label minimum) don't overlap. Earlier
// events land on the upper rows; compact items always stack below the rest.
export const organizeSegmentsInRows = <T extends SegmentItem>(
	weekSegments: WeekSegment<T>[],
): WeekSegment<T>[][] => [
	...packRows(
		weekSegments.filter((s) => !s.item.compact),
		MIN_VISUAL_HOURS,
	),
	...packRows(
		weekSegments.filter((s) => s.item.compact),
		MIN_VISUAL_HOURS_COMPACT,
	),
];

// Left offset and width of a segment, in % of one day cell
export const segmentGeometry = (seg: WeekSegment<SegmentItem>) => {
	const tz = serverTz();
	const dayPercent = (d: Date) => {
		const m = moment.tz(d, tz);
		return ((m.hour() * 60 + m.minute()) / (24 * 60)) * 100;
	};

	const startPercent = seg.isStart ? dayPercent(seg.item.start) : 0;
	const endPercent = seg.isEnd ? dayPercent(seg.item.end) : 100;

	const widthPercent =
		seg.spanDays === 1
			? endPercent - startPercent
			: 100 - startPercent + (seg.spanDays - 2) * 100 + endPercent;

	return { startPercent, widthPercent };
};

// Server-tz day keys a window touches (End exclusive by a minute)
export const dayKeysBetween = (start: Date, end: Date): string[] => {
	const tz = serverTz();
	const first = moment.tz(start, tz).startOf("day");
	const last = moment.tz(end, tz).subtract(1, "minute").startOf("day");
	const keys: string[] = [];
	const cursor = first.clone();
	while (cursor.isSameOrBefore(last, "day")) {
		keys.push(cursor.format("YYYY-MM-DD"));
		cursor.add(1, "day");
	}
	return keys.length ? keys : [moment.tz(start, tz).format("YYYY-MM-DD")];
};

export const formatDateTime = (d: Date) =>
	moment.tz(d, serverTz()).format("MM/DD/YY HH:mm");

/** Day once, then the hours: "09/17/26" + "06:00 → 11:30" (the end carries its
 *  own date only when it falls on another day) */
export const formatWindowParts = (start: Date, end: Date) => {
	const tz = serverTz();
	const s = moment.tz(start, tz);
	const e = moment.tz(end, tz);
	return {
		day: s.format("MM/DD/YY"),
		hours: `${s.format("HH:mm")} → ${e.format(
			s.isSame(e, "day") ? "HH:mm" : "MM/DD/YY HH:mm",
		)}`,
	};
};

export const formatTimeRange = (start: Date, end: Date) => {
	const tz = serverTz();
	return `${moment.tz(start, tz).format("MM/DD HH:mm")} - ${moment
		.tz(end, tz)
		.format("MM/DD HH:mm")}`;
};

export type DayKind = "8h" | "12h";

/** A line-day's scheme shape. Scheme ASSET ids are open-ended (a line can own
 *  its scheme, e.g. 20th Street Saw = 24), so 8h vs 12h is read off the day's
 *  shift count: 3 = 8h. */
export const dayKindOf = (
	rows: readonly CalendarDayShift[] | undefined,
): DayKind | null =>
	rows && rows.length > 0 ? (rows.length >= 3 ? "8h" : "12h") : null;

export const shiftsPerDayOf = (kind: DayKind): 2 | 3 => (kind === "8h" ? 3 : 2);

/** one shift's slice of a calendar day, precomputed for cell painting */
export interface ShiftBand {
	label: string;
	leftPct: number;
	widthPct: number;
	startMs: number;
	endMs: number;
	/** the day runs a pinned config instead of the line default */
	isOverride: boolean;
}

/** Shift slices per calendar day. A night shift spills into the next day's
 *  cell, so one shift can yield a band in two days; startMs/endMs stay the
 *  full shift bounds so a drop preview can tell which shift it lands in. */
export const buildShiftBands = (
	dayShifts: readonly CalendarDayShift[] | undefined,
	tz: string,
): Map<string, ShiftBand[]> => {
	const map = new Map<string, ShiftBand[]>();
	for (const ds of dayShifts ?? []) {
		const label = ds.Shift?.charAt(0) ?? "";
		const startMs = moment.utc(ds.Start).valueOf();
		const endMs = moment.utc(ds.End).valueOf();
		let cur = moment.utc(ds.Start).tz(tz);
		const end = moment.utc(ds.End).tz(tz);
		while (cur.isBefore(end)) {
			const dayStart = cur.clone().startOf("day");
			const dayEnd = dayStart.clone().add(1, "day");
			const sliceEnd = end.isBefore(dayEnd) ? end : dayEnd;
			// Measured, not assumed 24h: DST days are 23 or 25 hours wide
			const dayMs = dayEnd.diff(dayStart);
			const key = dayStart.format("YYYY-MM-DD");
			const list = map.get(key) ?? [];
			list.push({
				label,
				leftPct: (cur.diff(dayStart) / dayMs) * 100,
				widthPct: (sliceEnd.diff(cur) / dayMs) * 100,
				startMs,
				endMs,
				isOverride: ds.IsOverride,
			});
			map.set(key, list);
			cur = sliceEnd;
		}
	}
	return map;
};

export const formatDuration = (start: Date, end: Date) =>
	moment
		.duration(end.getTime() - start.getTime(), "milliseconds")
		.format("w [weeks], d [days], h [hours], m [minutes]", { trim: "both" });

/** Compact form for bar labels: the two largest units, pluralized
 *  ("2 hours", "3 days 12 hours", "1 week 1 day") */
export const formatDurationHuman = (start: Date, end: Date): string => {
	let mins = Math.round((end.getTime() - start.getTime()) / 60000);
	const units = [
		{ label: "week", size: 10080 },
		{ label: "day", size: 1440 },
		{ label: "hour", size: 60 },
		{ label: "min", size: 1 },
	];
	const parts: string[] = [];
	for (const u of units) {
		const n = Math.floor(mins / u.size);
		if (n > 0) {
			parts.push(`${n} ${u.label}${n > 1 && u.label !== "min" ? "s" : ""}`);
			mins -= n * u.size;
		}
		if (parts.length === 2) break;
	}
	return parts.length ? parts.join(" ") : "0 min";
};

/**
 * First free window at or after `preferredStart`: start where whatever is
 * already planned there ends, and cap the end where the next one begins, so a
 * suggested window never opens on top of an existing event.
 */
export const suggestFreeWindow = ({
	preferredStart,
	preferredMs,
	lowerBound,
	busy,
}: {
	preferredStart: number;
	preferredMs: number;
	lowerBound: number;
	busy: { s: number; e: number }[];
}): { start: Date; end: Date } => {
	let cursor = Math.max(preferredStart, lowerBound);
	let gapEnd: number | null = null;
	for (const w of [...busy].sort((a, b) => a.s - b.s)) {
		if (w.e <= cursor) continue;
		if (w.s > cursor) {
			gapEnd = w.s;
			break;
		}
		cursor = w.e;
	}
	return {
		start: new Date(cursor),
		end: new Date(Math.min(cursor + preferredMs, gapEnd ?? Number.MAX_VALUE)),
	};
};
